import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Alert, Typography, Space, Spin } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

const { Title, Text } = Typography;

const LoginPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const onFinish = async (values) => {
    setLoading(true);
    setError('');

    try {
      await login(values.usuario, values.password);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Error en login:', error);
      setError(error.response?.data?.error || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <Card className="login-card" bordered={false}>
          <div className="login-header">
            <div className="logo-container">
              <img 
                src="/images/horeb-logo.png" 
                alt="Horeb Apps" 
                className="login-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <Title level={2} className="login-title">
              Portal Administrativo
            </Title>
            <Text type="secondary" className="login-subtitle">
              Sistema de Gestión Multitenant
            </Text>
          </div>

          {error && (
            <Alert
              message="Error de Autenticación"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              className="login-error"
            />
          )}

          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            autoComplete="off"
            size="large"
            className="login-form"
          >
            <Form.Item
              name="usuario"
              rules={[
                {
                  required: true,
                  message: 'Por favor ingresa tu usuario o email'
                }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Usuario o Email"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Por favor ingresa tu contraseña'
                }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Contraseña"
                autoComplete="current-password"
                iconRender={(visible) => 
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                className="login-button"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>
            </Form.Item>
          </Form>

          <div className="login-footer">
            <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Horeb Apps © 2025 - Gestión de Conjuntos Residenciales
              </Text>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                v2.0.0 | Multitenant System
              </Text>
            </Space>
          </div>
        </Card>

        {loading && (
          <div className="login-overlay">
            <Spin size="large" />
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;